import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {DefaultProjectPickerAnchorProps} from '@github-ui/item-picker-project'
import {DefaultProjectPickerAnchor as FutureDefaultProjectPickerAnchor} from '@github-ui/item-picker-project'
import {
  BulkProjectPicker as FutureBulkProjectPicker,
  type BulkProjectPickerProps as FutureBulkProjectPickerProps,
  fetchProjects as futureFetchProjects,
  ProjectPickerGraphqlQuery as FutureProjectPickerGraphqlQuery,
  ProjectPickerRelay as FutureProjectPicker,
  type ProjectPickerRelayProject as FutureProjectPickerProject,
  type ProjectPickerRelayProps as FutureProjectPickerProps,
  type ProjectPickerTestQueries as FutureProjectPickerTestQueries,
  readProjectFromFragment as futureReadProjectFromFragment,
} from '@github-ui/item-picker-project-relay'

import {
  BulkProjectPicker as LegacyBulkProjectPicker,
  DefaultProjectPickerAnchor as LegacyDefaultProjectPickerAnchor,
  fetchProjects as legacyFetchProjects,
  ProjectPicker as LegacyProjectPicker,
  readProjectFromFragment as legacyReadProjectFromFragment,
} from './ProjectPicker'

/**
 * This module provides a unified interface for the ProjectPicker component,
 * allowing it to switch between legacy and future implementations based on feature flags.
 * It exports the ProjectPicker, BulkProjectPicker, DefaultProjectPickerAnchor,
 * and utility functions for reading project fragments and fetching projects.
 *
 * The feature flag 'item_picker_project_relay_migration' controls which implementation is used.
 *
 * Both versions of the ProjectPicker should be functionally equivalent, and both use Relay for data fetching.
 */

export type ProjectPickerProject = FutureProjectPickerProject

export const ProjectPickerGraphqlQuery = FutureProjectPickerGraphqlQuery

export type ProjectPickerTestQueries = FutureProjectPickerTestQueries

export function DefaultProjectPickerAnchor(props: DefaultProjectPickerAnchorProps) {
  const ffEnabled = isFeatureEnabled('item_picker_project_relay_migration')
  if (ffEnabled) return <FutureDefaultProjectPickerAnchor {...props} />
  return <LegacyDefaultProjectPickerAnchor {...props} />
}

export function ProjectPicker(props: FutureProjectPickerProps) {
  const ffEnabled = isFeatureEnabled('item_picker_project_relay_migration')
  if (ffEnabled) return <FutureProjectPicker {...props} />
  return <LegacyProjectPicker {...props} />
}

// Accept a superset; at runtime we branch and cast.
export function BulkProjectPicker(props: FutureBulkProjectPickerProps) {
  const ffEnabled = isFeatureEnabled('item_picker_project_relay_migration')
  if (ffEnabled) return <FutureBulkProjectPicker {...props} />
  return <LegacyBulkProjectPicker {...props} />
}

type FutureProjectFragmentKey = Parameters<typeof futureReadProjectFromFragment>[0]
type LegacyProjectFragmentKey = Parameters<typeof legacyReadProjectFromFragment>[0]

export function readProjectFromFragment(
  fragmentKey: FutureProjectFragmentKey | LegacyProjectFragmentKey,
): ProjectPickerProject {
  const ffEnabled = isFeatureEnabled('item_picker_project_relay_migration')
  if (ffEnabled) return futureReadProjectFromFragment(fragmentKey as FutureProjectFragmentKey)
  return legacyReadProjectFromFragment(fragmentKey as LegacyProjectFragmentKey)
}

export function fetchProjects(
  environment: Parameters<typeof futureFetchProjects>[0],
  options: Parameters<typeof futureFetchProjects>[1],
): ReturnType<typeof futureFetchProjects> {
  const ffEnabled = isFeatureEnabled('item_picker_project_relay_migration')
  if (ffEnabled) return futureFetchProjects(environment, options)
  return legacyFetchProjects(environment, options)
}

try{ DefaultProjectPickerAnchor.displayName ||= 'DefaultProjectPickerAnchor' } catch {}
try{ ProjectPicker.displayName ||= 'ProjectPicker' } catch {}
try{ BulkProjectPicker.displayName ||= 'BulkProjectPicker' } catch {}